import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressBarModule } from '@angular/material/progress-bar';

import { AlertCardComponent } from '../../../../../../shared/components/alert-card/alert-card.component';
import { ImportarCategoriaFinanceiraService } from '../../../../../../shared/services/apis/financas/importar-categoria-financeira.service';
import { AlertService, AlertType } from '../../../../../../shared/services/alert.service';
import { ImportarArquivoRequest } from '../../../../../../shared/services/requests/financas/importar-arquivo.request';
import { ValidacaoImportacaoCategoriaFinanceiraResponse } from '../../../../../../shared/services/responses/financas/validacao-importacao-categoria-financeira.response';
import { ItemValidacaoCategoriaFinanceiraStruct } from '../../../../../../shared/services/structs/financas/item-validacao-categoria-financeira.struct';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AcaoImportacaoEnum } from '../../../../../../shared/enums/financas/acao-importacao.enum';

// Tipo para controlar o filtro ativo visualmente
type FilterType = 'todos' | 'novos' | 'atualizados' | 'avisos' | 'erros';

@Component({
  selector: 'app-dialog-importar-categorias-financeiras',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatProgressBarModule,
    HttpClientModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './dialog-importar-categorias-financeiras.component.html',
  styleUrls: ['./dialog-importar-categorias-financeiras.component.css']
})
export class DialogImportarCategoriasFinanceirasComponent implements OnInit {

  public selectedFile: File | null = null;
  public errorMessage: string | null = null;
  public MAX_SIZE_MB = 2;

  public _acaoImportacaoEnum = AcaoImportacaoEnum;

  public step: number = 1;
  public isLoading: boolean = false;

  // Dados de Validação
  public validationResponse: ValidacaoImportacaoCategoriaFinanceiraResponse | null = null;

  // Lista filtrada que será exibida na tabela
  public itemsFiltered: ItemValidacaoCategoriaFinanceiraStruct[] = [];
  public currentFilter: FilterType = 'todos';

  public dataSourceExample: any[] = [];

  public allColumns: string[] = [
    'id', 'descricao', 'tipoCategoriaFinanceiraDescricao', 'nomeCategoriaFinanceiraPai', 'tipoCategoriaDreDescricao'
  ];

  public displayedColumnsExample: string[] = [...this.allColumns];
  public displayedColumnsPreview: string[] = ['status', ...this.allColumns];

  constructor(
    public dialogRef: MatDialogRef<DialogImportarCategoriasFinanceirasComponent>,
    private importarCategoriaFinanceiraService: ImportarCategoriaFinanceiraService,
    private alertService: AlertService,
    private http: HttpClient
  ) { }

  ngOnInit(): void {
    this.carregarExemploCSV();
  }

  // --- Carregamento do CSV de Exemplo ---
  carregarExemploCSV(): void {
    this.http.get('assets/files/exemplo_planilha_categoria_financeira.csv', { responseType: 'text' })
      .subscribe({
        next: (data) => { this.parseCSV(data); },
        error: (err) => { console.error('Erro ao carregar CSV de exemplo', err); }
      });
  }

  parseCSV(data: string): void {
    const lines = data.split('\n');
    if (!lines || lines.length < 2) return;

    const result = [];
    for (let i = 1; i < Math.min(lines.length, 6); i++) {
      const line = lines[i].trim();
      if (!line) continue;
      const cols = line.split(';');

      const obj: any = {
        id: cols[0],
        descricao: cols[1],
        tipoCategoriaFinanceiraDescricao: cols[2],
        nomeCategoriaFinanceiraPai: cols[3],
        tipoCategoriaDreDescricao: cols[4]
      };
      result.push(obj);
    }
    this.dataSourceExample = result;
  }

  // --- Step 1: Upload ---
  onFileSelected(event: any): void {
    const file = event.target.files[0];
    this.processFile(file);
    event.target.value = '';
  }

  processFile(file: File | undefined): void {
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      this.errorMessage = 'Apenas arquivos .csv são permitidos.';
      this.selectedFile = null;
      return;
    }

    if (file.size > this.MAX_SIZE_MB * 1024 * 1024) {
      this.errorMessage = `O arquivo deve ter no máximo ${this.MAX_SIZE_MB}MB.`;
      this.selectedFile = null;
      return;
    }

    this.errorMessage = null;
    this.selectedFile = file;
    this.processarArquivo();
  }

  downloadModelo(): void {
    const link = document.createElement('a');
    link.href = 'assets/files/exemplo_planilha_categoria_financeira.csv';
    link.download = 'exemplo_planilha_categoria_financeira.csv';
    link.click();
  }

  cancelar(): void {
    this.dialogRef.close();
  }

  voltarParaUpload(): void {
    this.step = 1;
    this.selectedFile = null;
    this.validationResponse = null;
    this.errorMessage = null;
    this.itemsFiltered = []; // Limpa lista
  }

  // --- Step 2: Validação e Lógica ---
  processarArquivo(): void {
    if (!this.selectedFile) return;

    this.isLoading = true;
    const reader = new FileReader();
    reader.readAsDataURL(this.selectedFile);

    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1];
      this.enviarParaValidacao(base64);
    };

    reader.onerror = (error) => {
      this.isLoading = false;
      this.alertService.show('Erro', 'Erro ao ler arquivo.', AlertType.error);
    };
  }

  private enviarParaValidacao(base64: string): void {
    const request = new ImportarArquivoRequest();
    request.arquivoBase64 = base64;

    this.importarCategoriaFinanceiraService.validar(request).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response.isError) {
          this.alertService.show('Erro na validação', response.errorDescription, AlertType.error);
          this.selectedFile = null;
        } else {
          this.validationResponse = response;
          this.step = 2;
          // Inicializa o filtro com 'todos' para popular a tabela imediatamente
          this.aplicarFiltro('todos');
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.selectedFile = null;
        this.alertService.show('Erro', 'Falha ao processar arquivo.', AlertType.error);
      }
    });
  }

  // --- Filtros ---
  aplicarFiltro(tipo: FilterType): void {
    this.currentFilter = tipo;
    if (!this.validationResponse) return;

    const itens = this.validationResponse.itens;

    switch (tipo) {
      case 'todos':
        this.itemsFiltered = itens;
        break;
      case 'novos':
        this.itemsFiltered = itens.filter(i => i.acao == AcaoImportacaoEnum.Novo);
        break;
      case 'atualizados':
        this.itemsFiltered = itens.filter(i => i.acao == AcaoImportacaoEnum.Atualizar);
        break;
      case 'avisos':
        this.itemsFiltered = itens.filter(i => i.isAlerta);
        break;
      case 'erros':
        this.itemsFiltered = itens.filter(i => !i.isValido || i.erros.length > 0);
        break;
    }
  }

  confirmarImportacao(): void {
    if (this.isImportDisabled) return;

    if (this.isLoading) return;

    const itensValidos = this.validationResponse!.itens.filter(i => i.isValido);

    this.isLoading = true;
    this.importarCategoriaFinanceiraService.confirmar(itensValidos).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response.isError) {
          this.alertService.show('Erro na Gravação', response.errorDescription, AlertType.error);
        } else {
          this.alertService.show('Sucesso', 'Importação finalizada com sucesso.', AlertType.success);
          this.dialogRef.close(true);
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Falha ao confirmar importação.', AlertType.error);
      }
    });
  }

  // --- Helpers ---
  getResumoTotal(): number { return this.validationResponse?.itens?.length || 0; }
  getResumoNovos(): number { return this.validationResponse?.resumo?.totalNovos || 0; }
  getResumoAtualizados(): number { return this.validationResponse?.resumo?.totalAtualizacoes || 0; }
  getResumoAvisos(): number { return this.validationResponse?.resumo?.totalAvisos || 0; }
  getResumoErros(): number { return this.validationResponse?.resumo?.totalErros || 0; }

  get isImportDisabled(): boolean {
    if (!this.validationResponse) return true;
    if (this.getResumoErros() > 0) return true;
    if (this.getResumoTotal() === 0) return true;
    return false;
  }

  getButtonTooltip(): string {
    if (this.getResumoErros() > 0) {
      return 'Resolva os erros e importe a planilha novamente para seguir com a importação';
    }
    return '';
  }

  getTooltip(item: ItemValidacaoCategoriaFinanceiraStruct): string {
    const parts = [];
    if (item.erros?.length) parts.push(`ERROS: ${item.erros.join(', ')}`);
    if (item.avisos?.length) parts.push(`AVISOS: ${item.avisos.join(', ')}`);

    if (parts.length === 0) {
      if (item.acao === AcaoImportacaoEnum.Novo) return 'Nova Categoria Financeira';
      if (item.acao === AcaoImportacaoEnum.Atualizar) return 'Atualização de Cadastro';
    }
    return parts.join('\n');
  }

  getRowClass(item: ItemValidacaoCategoriaFinanceiraStruct): string {
    if (!item.isValido) return 'row-error';
    if (item.isAlerta) return 'row-warning';
    return '';
  }
}
